import React, { ChangeEvent, Dispatch, useEffect, useState } from 'react'
import { Card, Col, Divider, Input, InputNumber, Row, Typography } from 'antd'
import { s } from './style'
import { useTranslation } from 'react-i18next'
import lodash from 'lodash'
import n from 'numeral'
import { OptionalExtras } from './OptionalExtras'
import { CURRENCY, NUMERAL_FORMAT } from '../../../Constants'
import { CostBlock, TotalBlock } from './CostBlock'
import useGlobal, { GlobalState, GlobalActions } from '../../../Store'
import { formatCents } from '../../../Utilities'
import { useDateTimeDuration } from '../../../Hooks'
import { DetailsCardProposalFragment } from './DetailsCard.generated-types'
import ReactMarkdown from 'react-markdown'

const { TextArea } = Input
const { Text, Title } = Typography

interface DetailsCardProps {
  proposal: DetailsCardProposalFragment
  editing: boolean
  setProposal: Dispatch<any>
}

export const DetailsCard: React.FC<DetailsCardProps> = ({
  proposal,
  proposal: { ID, attendees, baseCostTotal, baseCostDescription, extraCostItems, currency },
  editing,
  setProposal,
}) => {
  const { t } = useTranslation()

  const { durationFullDate, durationRange } = useDateTimeDuration(proposal)
  const costItemsByIDs = lodash.keyBy(extraCostItems, 'ID')
  const [globalState, globalActions] = useGlobal<GlobalState, GlobalActions>()
  const [total, setTotal] = useState(0)

  useEffect(() => {
    if (!globalState[ID]) {
      extraCostItems.forEach(({ ID: ItemID }) => {
        globalActions.setBookedItems({
          ID,
          bookedItems: {
            [ItemID]: { ID: ItemID, quantity: 0 },
          },
        })
      })
    }
  }, [])

  useEffect(() => {
    const total = lodash.reduce(
      globalState[ID]?.bookedItems,
      (total, bookedItem) => {
        const costPerUnit = costItemsByIDs[bookedItem.ID]?.costPerUnit || 0
        return total + costPerUnit * bookedItem.quantity
      },
      0
    )
    setTotal(total)
  }, [globalState[ID]])

  const handleUpdateBaseCostTotal = (newBaseCostTotal: number): void => {
    const withPence = newBaseCostTotal * 100

    setProposal({
      ...proposal,
      baseCostTotal: withPence,
    })
  }

  const handleUpdateBaseCostDescription = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setProposal({ ...proposal, baseCostDescription: event.target.value })
  }

  return (
    <Card style={{ minWidth: 330 }}>
      <Row style={s.spacedRow}>
        <Title style={s.detail} level={4}>
          {t('proposal.details.title', 'Details')}
        </Title>
      </Row>
      <Row style={s.spacedRow}>
        <Col span={14}>
          <Text style={s.lightText}>{t('proposal.requestToBook.timings', 'Timings')}</Text>
          <br />
          <Text style={s.labelText} strong>
            {durationFullDate}
          </Text>
          <br />
          <Text style={s.midText}>{durationRange}</Text>
        </Col>
        <Col span={7}>
          <Text style={s.lightText}>{t('proposal.requestToBook.attendees', 'Attendees')}</Text>
          <br />
          <Text strong style={s.labelText}>
            {n(attendees).format(NUMERAL_FORMAT)}
          </Text>
        </Col>
      </Row>
      <Row style={s.spacedRow}>
        <Col span={14}>
          <Text style={s.lightText}>{t('proposal.requestToBook.fixedCosts', 'Fixed costs')}</Text>
          <br />
          {editing ? (
            <InputNumber
              defaultValue={baseCostTotal / 100}
              prefix={CURRENCY[currency]}
              onChange={handleUpdateBaseCostTotal}
            />
          ) : (
            <Text style={s.labelText}>{formatCents(currency, baseCostTotal)}</Text>
          )}
        </Col>
      </Row>
      <Row style={s.spacedRow}>
        {editing ? (
          <TextArea
            defaultValue={baseCostDescription}
            onChange={handleUpdateBaseCostDescription}
            autoSize
          />
        ) : (
          <Text style={s.msgText}>
            <ReactMarkdown>{baseCostDescription}</ReactMarkdown>
          </Text>
        )}
      </Row>
      {extraCostItems.length > 0 && (
        <Row style={s.spacedRow}>
          <Col span={24}>
            <Row style={s.spacedRow}>
              <Text style={s.lightText}>
                {t('proposal.requestToBook.optionalExtras', 'Optional extras')}
              </Text>
            </Row>
            <OptionalExtras proposal={proposal} editing={editing} setProposal={setProposal} />
          </Col>
        </Row>
      )}
      <Divider />
      <CostBlock title={t('proposal.requestToBook.hireCost', 'Fixed costs')}>
        {formatCents(currency, baseCostTotal)}
      </CostBlock>
      <CostBlock title={t('proposal.requestToBook.optionalExtras', 'Optional extras')}>
        {formatCents(currency, total)}
      </CostBlock>
      <Divider />
      <TotalBlock title={t('proposal.requestToBook.total', 'Total (ex VAT)')}>
        {formatCents(currency, baseCostTotal + total)}
      </TotalBlock>
    </Card>
  )
}
